import { Button, Card, makeStyles, Text, tokens } from '@fluentui/react-components';
import { ArrowClockwise20Regular, PersonArrowRight20Regular } from '@fluentui/react-icons';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { ActivationRequiredReason, SpirisService } from '../../../libs/services/SpirisService';
import { useAppSelector } from '../../../redux/app/hooks';
import { RootState } from '../../../redux/app/store';
import { SpirisActivationSteps } from '../../shared/SpirisActivationSteps';

const useStyles = makeStyles({
    card: {
        backgroundColor: tokens.colorNeutralBackground2,
        borderLeft: `4px solid ${tokens.colorBrandStroke1}`,
        padding: tokens.spacingVerticalM,
        marginTop: tokens.spacingVerticalS,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: tokens.spacingVerticalS,
    },
    title: {
        fontWeight: tokens.fontWeightSemibold,
    },
    body: {
        color: tokens.colorNeutralForeground2,
    },
    error: {
        color: tokens.colorPaletteRedForeground1,
    },
    buttons: {
        display: 'flex',
        gap: tokens.spacingHorizontalS,
    },
});

interface SpirisConnectCardProps {
    reason?: string | null;
    // The configured Visma My Services base URL where the MCP is turned on (activation_required only).
    activationUrl?: string | null;
    // The user message that needed Spiris; offered for a one-click retry once the MCP is turned on.
    retryMessage?: string;
    onRetry: (message: string) => void;
}

export const SpirisConnectCard: React.FC<SpirisConnectCardProps> = ({
    reason,
    activationUrl,
    retryMessage,
    onRetry,
}) => {
    const styles = useStyles();
    const { t } = useTranslation();
    const auth = useAuth();
    const token = auth.user?.access_token;
    const hostContext = useAppSelector((state: RootState) => state.hostContext.hostContext);
    const [activation, setActivation] = useState<'idle' | 'checking' | 'stillInactive' | 'error'>('idle');

    if (!token) {
        return null;
    }

    // Signing in again does not help here: the company must turn access on in Spiris first. The
    // server remembers the refusal, so check again before resending — otherwise the retry would skip the MCP.
    const recheckAndRetry = async (message: string) => {
        setActivation('checking');
        try {
            const connection = await new SpirisService(token).recheckConnectionAsync(hostContext);
            if (connection.connected) {
                setActivation('idle');
                onRetry(message);
            } else {
                setActivation('stillInactive');
            }
        } catch {
            setActivation('error');
        }
    };

    if (reason === ActivationRequiredReason) {
        return (
            <Card className={styles.card} aria-live="polite" data-testid="spiris-connect-card">
                <Text className={styles.title}>{t('spiris.connect.activationTitle')}</Text>
                <Text className={styles.body}>{t('spiris.connect.activationBody')}</Text>
                <SpirisActivationSteps activationUrl={activationUrl} />
                <div className={styles.buttons}>
                    {retryMessage && (
                        <Button
                            icon={<ArrowClockwise20Regular />}
                            disabled={activation === 'checking'}
                            onClick={() => {
                                void recheckAndRetry(retryMessage);
                            }}
                            data-testid="spiris-connect-retry"
                        >
                            {t('spiris.connect.retry')}
                        </Button>
                    )}
                </div>
                {activation === 'stillInactive' && (
                    <Text className={styles.body}>{t('spiris.connect.stillInactive')}</Text>
                )}
                {activation === 'error' && <Text className={styles.error}>{t('spiris.connect.error')}</Text>}
            </Card>
        );
    }

    // Any other reason is a missing or rejected grant: the MCP scope comes with the sign-in token.
    return (
        <Card className={styles.card} aria-live="polite" data-testid="spiris-connect-card">
            <Text className={styles.title}>{t('spiris.connect.title')}</Text>
            <Text className={styles.body}>{t('spiris.connect.signinBody')}</Text>
            <Button
                appearance="primary"
                icon={<PersonArrowRight20Regular />}
                onClick={() => {
                    void auth.signinRedirect();
                }}
                data-testid="spiris-connect-button"
            >
                {t('spiris.connect.signIn')}
            </Button>
        </Card>
    );
};
